// The meetings vocabulary.
//
// Each `as const` array is the ONE source for its value set: the Postgres
// CHECK constraints in `packages/db/src/schema/app.ts` are built from these
// arrays, so a new value here shows up as migration drift
// (`db-migrate-check`), never as a CHECK that silently refuses a write. Add a
// value here first, then generate the migration.
//
// Pure data: no I/O, no crypto, no regex lookbehind (the web imports this).

/** A meeting's lifecycle, as the list and the review page show it. */
export const MEETING_STATUSES = [
  "scheduled",
  "in_meeting",
  "skipped",
  "recorded",
  "no_recording",
  "transcribing",
  "needs_project",
  "triaging",
  "filing",
  "ready",
  "failed",
] as const;
export type MeetingStatus = (typeof MEETING_STATUSES)[number];

/**
 * The processing run's steps, in order (`meeting_run.step`). The job moves a
 * run forward one step per transaction and checkpoints each step's output in
 * `meeting_stage`. `await_project` parks a meeting with no project;
 * `snapshot` asks the server for the project's open issues (the job holds no
 * forge token). The triage is the line-tag pipeline (owner decision
 * 2026-10-16): `inventory` (the subject catalogue), `tag` (every line, per
 * window), `ground`, `assemble` and `classify` (code), then `draft`, `judge`
 * and `dedupe`, which writes the rows. `ready_to_file` hands over to the
 * server's filing; `done` and `failed` are final.
 */
export const RUN_STEPS = [
  "fetch",
  "timing",
  "asr",
  "align",
  "await_project",
  "snapshot",
  "inventory",
  "tag",
  "ground",
  "assemble",
  "classify",
  "draft",
  "judge",
  "dedupe",
  "ready_to_file",
  "done",
  "failed",
] as const;
export type RunStep = (typeof RUN_STEPS)[number];

/**
 * Steps of the one-topic triage the line tags replaced (`extract → verify`).
 * No run moves to them any more; a run still sitting at one is moved on to
 * `inventory` when it is claimed (`run.ts`), and the stages they wrote stay
 * until `data-retention` sweeps them. They stay in the CHECKs only, so those
 * rows remain valid.
 */
export const RETIRED_RUN_STEPS = ["extract", "verify"] as const;
export type RetiredRunStep = (typeof RETIRED_RUN_STEPS)[number];

/** Every value `meeting_run.step` and `meeting_stage.step` may hold: the CHECK. */
export const STORED_RUN_STEPS = [...RUN_STEPS, ...RETIRED_RUN_STEPS] as const;
export type StoredRunStep = (typeof STORED_RUN_STEPS)[number];

export const isRetiredRunStep = (step: string): step is RetiredRunStep =>
  (RETIRED_RUN_STEPS as readonly string[]).includes(step);

/** `meeting_space.status`. */
export const SPACE_STATUSES = ["active", "pending_takeover", "ended", "host_lost"] as const;
export type SpaceStatus = (typeof SPACE_STATUSES)[number];

/**
 * `meeting_space.kind`: Loom created it, an activated series / single event,
 * or `imported`: the Google space of a meeting the owner recorded without
 * Loom and picked afterwards ("Aufgenommenes Meeting verarbeiten"). An
 * imported space is always `ended`: Loom never reads or writes its config,
 * and the reconcile never stores a later conference in it.
 */
export const SPACE_KINDS = ["created", "series", "single", "imported"] as const;
export type SpaceKind = (typeof SPACE_KINDS)[number];

/**
 * `meeting_space.recording_start`: how recording starts in the space.
 * `auto`: Meet records and transcribes by itself once someone allowed to
 * record joins (auto artifacts ON). `manual`: auto artifacts OFF; the host
 * starts "Aufzeichnen" and "Transkript" in Meet by hand, after telling the
 * others. A launch defaults to `manual`.
 */
export const RECORDING_STARTS = ["auto", "manual"] as const;
export type RecordingStart = (typeof RECORDING_STARTS)[number];

/** `meeting_recording.state`, Google's `Recording.state` lower-cased. */
export const RECORDING_STATES = ["started", "ended", "file_generated"] as const;
export type RecordingState = (typeof RECORDING_STATES)[number];

/** `meeting.recording_flag`; null means nothing to flag. */
export const RECORDING_FLAGS = ["not_running", "incomplete"] as const;
export type RecordingFlag = (typeof RECORDING_FLAGS)[number];

/**
 * `meeting_ticket.state`. `filing` is the CAS claim; `filed` is final.
 * `proposed`: a row of a reprocess run ("Neu verarbeiten", owner decision
 * 2026-10-18): drafted like any other, but filed only when the owner asks
 * ("Anlegen" moves it to `drafted` and files that one row). Nothing files a
 * `proposed` row on its own; for the meeting's `ready` it is final.
 * `withdrawn`: never filed, because the answer to its doubt said to file
 * nothing ("Nichts anlegen" before filing). Final; never sent in a DTO.
 */
export const TICKET_STATES = [
  "drafted",
  "filing",
  "filed",
  "draft_failed",
  "file_failed",
  "proposed",
  "withdrawn",
] as const;
export type TicketState = (typeof TICKET_STATES)[number];

/**
 * `meeting_ticket.update_state`: "Ticket aktualisieren" (owner decision
 * 2026-10-19) on a current row that is an issue the meeting already
 * filed ("Schon angelegt als #N"). The row is the command and its id the
 * idempotency key: one update per issue per revision. `updating` is the CAS
 * claim (the body write and the note run under it), `updated` is final (a
 * second click writes nothing), `failed` may be clicked again. Null: never
 * asked.
 */
export const TICKET_UPDATE_STATES = ["updating", "updated", "failed"] as const;
export type TicketUpdateState = (typeof TICKET_UPDATE_STATES)[number];

/** `meeting_ticket.class`: what a filed topic became. */
export const TICKET_CLASSES = ["ticket", "epic"] as const;
export type TicketClass = (typeof TICKET_CLASSES)[number];

/** `meeting_ticket.reason`: why `classifyTopic` (or the owner) filed it. */
export const TICKET_REASONS = ["committed", "requested", "too_broad", "idea", "promoted"] as const;
export type TicketReason = (typeof TICKET_REASONS)[number];

/**
 * `meeting_topic.reason`: why a topic became no issue ("Nicht übernommen").
 * The line tags add three: `question` (an open question and nothing to
 * build), `process` (how the team works together, a reply owed: never an
 * issue) and `doubt` (Loom asks before it decides: the item is held until
 * the owner answers, owner decision 2026-10-16, 2a).
 */
export const TOPIC_REASONS = [
  "ungrounded",
  "other_project",
  "unclear_project",
  "already_settled",
  "status_update",
  "decision_no_work",
  "chatter",
  "unaccounted",
  "question",
  "process",
  "doubt",
] as const;
export type TopicReason = (typeof TOPIC_REASONS)[number];

/** What a work item classifies as: no issue (`none`, a "Nicht übernommen"
 *  row) or an issue class. `meeting_topic.held_class`. */
export const ITEM_CLASSES = ["none", ...TICKET_CLASSES] as const;
export type ItemClass = (typeof ITEM_CLASSES)[number];

/** Every reason an item's main outcome can carry: `meeting_topic.held_reason`,
 *  what a held item would have been without its doubt. */
export const ITEM_REASONS = [...TICKET_REASONS, ...TOPIC_REASONS] as const;
export type ItemReason = (typeof ITEM_REASONS)[number];

/** `meeting_topic.status_comment_state`: the status comment on `#N` (6.7). */
export const STATUS_COMMENT_STATES = [
  "none",
  "pending",
  "posting",
  "posted",
  "failed",
  "skipped",
] as const;
export type StatusCommentState = (typeof STATUS_COMMENT_STATES)[number];

/** `meeting_event.actor_kind`. */
export const MEETING_EVENT_ACTORS = ["owner", "link", "system"] as const;
export type MeetingEventActor = (typeof MEETING_EVENT_ACTORS)[number];

/**
 * `meeting_event.action`: the full list for every work package, so the CHECK
 * never needs a migration to grow. Content-free: an action names what
 * happened, never what was said.
 */
export const MEETING_EVENT_ACTIONS = [
  "google_connect",
  "google_disconnect",
  "host_lost",
  "approval_revoked",
  "launch",
  "import",
  "series_activate",
  "series_end",
  "split_detected",
  "takeover",
  "config_changed_by_human",
  "config_repatched",
  "skip",
  "unskip",
  "process",
  "link_create",
  "link_renew",
  "link_revoke",
  "link_use",
  "transcript_open",
  "accept",
  "decline",
  "restore",
  "promote",
  "edit",
  "class_switch",
  // Refine light: an open question answered, or "Weiss ich
  // nicht", on the meeting page or through the link. Never the text.
  "answer",
  "answer_skip",
  // The doubt card: the owner answered a doubt, releasing the
  // items it held or leaving them unfiled. The code is the answer.
  "doubt_answer",
  "retarget",
  "retarget_blocked",
  "file_ok",
  "file_fail",
  "status_comment_ok",
  "status_comment_fail",
  "retry",
  // "Neu verarbeiten": the owner re-ran the current triage on a finished
  // meeting. The code is the new revision (`r<n>`).
  "reprocess",
  // "Ticket aktualisieren": the owner rewrote an already filed issue's body
  // from the current revision and noted the meeting on it. The code is the
  // outcome (`ok`, `unchanged`, `failed:<code>`).
  "ticket_update",
  "delete",
  // A doubt's answer took effect on the issue it held (labels, body, close
  // or fold). The code is the effect and its outcome.
  "answer_apply",
  // A link between two issues of the meeting, or to an open issue a row
  // names, was written on the forge. The code is the outcome.
  "issue_link",
] as const;
export type MeetingEventAction = (typeof MEETING_EVENT_ACTIONS)[number];

const STEP_STATUS: Readonly<Record<StoredRunStep, MeetingStatus>> = {
  fetch: "transcribing",
  timing: "transcribing",
  asr: "transcribing",
  align: "transcribing",
  await_project: "needs_project",
  snapshot: "triaging",
  inventory: "triaging",
  tag: "triaging",
  ground: "triaging",
  assemble: "triaging",
  classify: "triaging",
  draft: "triaging",
  judge: "triaging",
  dedupe: "triaging",
  ready_to_file: "filing",
  done: "ready",
  failed: "failed",
  extract: "triaging",
  verify: "triaging",
};

/**
 * The meeting status a run step shows as (a retired one too: a run still
 * sitting there is triaging). The store writes it in the same transaction
 * that moves the step, so the list never disagrees with the run.
 */
export const meetingStatusFor = (step: StoredRunStep): MeetingStatus => STEP_STATUS[step];

/**
 * The kinds of information one transcript line gives about one subject (the
 * line-tag design, owner decision 2026-10-16): a request, an acceptance
 * detail, a bug, a decision, a rejection, something already done, a
 * commitment, a hedge, a question, a status report, a constraint, a
 * priority, context. A line can carry several. The eval harness's gold
 * labels use them (`apps/server/src/meetings/triage-score/`). No CHECK reads them.
 */
export const FACT_FUNCTIONS = [
  "REQ",
  "AC",
  "BUG",
  "DEC",
  "REJ",
  "DONE",
  "COM",
  "HEDGE",
  "Q",
  "STAT",
  "CON",
  "PRIO",
  "CTX",
] as const;
export type FactFunction = (typeof FACT_FUNCTIONS)[number];

/**
 * What a subject of a meeting is: `work` can become an issue; a `facet` is
 * part of one work item; a `source` (an e-mail, an earlier meeting) and a
 * `process` item (how the team works together) never file. No CHECK reads
 * them.
 */
export const SUBJECT_KINDS = ["work", "facet", "source", "process"] as const;
export type SubjectKind = (typeof SUBJECT_KINDS)[number];

/**
 * A typed link between two subjects of one meeting, grounded on one line:
 * one sets the other's priority, depends on it, is the alternative to it,
 * is the means for it, reports its status, gives advice on it, or owes it a
 * reply. An edge only lends draft context (`facts.ts`, supporting
 * evidence); it never decides an outcome. No CHECK reads them.
 */
export const EDGE_KINDS = [
  "prioritizes",
  "depends_on",
  "alternative_to",
  "means_for",
  "status_of",
  "advice_on",
  "reply_owed",
] as const;
export type EdgeKind = (typeof EDGE_KINDS)[number];

/**
 * What a commitment takes on: `work` on the product ("Den Filter baue
 * ich"), a `reply` ("Ich schreibe euch"), or something about how the team
 * works (`process`). Only a `work` commitment makes a ticket `committed`.
 */
export const COM_TARGETS = ["work", "reply", "process"] as const;
export type ComTarget = (typeof COM_TARGETS)[number];

/** Who made a commitment. The opening lines of a meeting can have no
 *  speakers, so `unknown` is a real answer, never a guess. */
export const COM_BY = ["agency", "customer", "unknown"] as const;
export type ComBy = (typeof COM_BY)[number];

/**
 * Where a line points outside the transcript: the shared `screen`, an
 * `earlier_meeting` (or this one's unrecorded start), an `external_doc`
 * (an e-mail, a list, a design). The eval's gold labels add `ana`, a
 * reference back inside the meeting (`triage-score/gold.ts`).
 */
export const REF_KINDS = ["screen", "earlier_meeting", "external_doc"] as const;
export type RefKind = (typeof REF_KINDS)[number];

/**
 * The doubts Loom raises instead of deciding silently (owner rule "unclear
 * means ask"): is this one item or several (`one_or_many`), are two items
 * the same (`same_or_two`), which project is it (`which_project`), what
 * does a reference outside the transcript mean (`unresolved_ref`), is an
 * idea floated with nobody on our side taking it on work for now or only
 * an idea (`work_or_idea`, owner decision 2026-10-19). A doubt holds only
 * the items it names (owner decision 2026-10-16, 2a). A held item with an
 * issue class is filed at once as its held class, with the doubt as an
 * open point in its body; the doubt stays an ask on the card, and its
 * answer then changes the filed issue (owner decision 2026-10-29).
 */
export const DOUBT_CODES = ["one_or_many", "same_or_two", "which_project", "unresolved_ref", "work_or_idea"] as const;
export type DoubtCode = (typeof DOUBT_CODES)[number];

/**
 * Which branch of its rule raised a doubt (`specs.ts` `doubtRule`,
 * `sameOrTwoDoubts`), for the classify step's counts: a settled item beside
 * an open bug or a root beside a single facet (`one_or_many`), no clear
 * project (`which_project`), a reference that rests outside the transcript
 * (`unresolved_ref`), an idea floated hedged or as a question
 * (`work_or_idea`), two items sharing lines (`same_or_two`). Content-free;
 * no CHECK reads them.
 */
export const DOUBT_BRANCHES = [
  "settled_beside_open_bug",
  "root_beside_one_facet",
  "project_unclear",
  "rests_outside",
  "floated_hedged",
  "floated_question",
  "shared_lines",
] as const;
export type DoubtBranch = (typeof DOUBT_BRANCHES)[number];

/**
 * The owner's answer on the doubt card (`meeting_topic.doubt_answer`), per
 * held item:
 * - `as_held`: what Loom would have made of it without the doubt
 *   (`held_class`): a ticket or an epic is filed, a `none` item stays under
 *   "Nicht übernommen" with its held reason;
 * - `ticket` / `epic`: filed as that class;
 * - `merged`: two items that are one ("Ein Thema"): one issue from both;
 * - `skip`: nothing is filed.
 * The question decides which answers it offers (`doubtAnswersFor`).
 */
export const MEETING_DOUBT_ANSWERS = ["as_held", "ticket", "epic", "merged", "skip"] as const;
export type MeetingDoubtAnswer = (typeof MEETING_DOUBT_ANSWERS)[number];

/**
 * `meeting_topic.carry` (0044): how a row carries an earlier revision's
 * doubt (its topic ids in `carried_from`) after a reprocess:
 * - `asking`: the earlier doubt nobody answered asks on this row;
 * - `asked_again`: the new triage raised the same doubt itself (lineage only);
 * - `answer_kept`: an earlier answer decided this row;
 * - `settled_prioritized` / `settled_agreed` / `settled_committed`: a
 *   `work_or_idea` doubt the new run settled by rule (a plain priority, an
 *   agreement or our side's commitment on the target), retired visibly.
 */
export const TOPIC_CARRIES = [
  "asking",
  "asked_again",
  "answer_kept",
  "settled_prioritized",
  "settled_agreed",
  "settled_committed",
] as const;
export type TopicCarry = (typeof TOPIC_CARRIES)[number];

/**
 * How a work item may use a line (`evidence.ts`): `primary` facts are
 * linked to the item itself and decide its outcome; `supporting` facts
 * reach it through a grounded edge and are draft context only.
 */
export const EVIDENCE_ROLES = ["primary", "supporting"] as const;
export type EvidenceRole = (typeof EVIDENCE_ROLES)[number];

/** What an item's evidence is asked for: deciding its class, its draft's
 *  context, the quotes it stores, the open questions' leak allowlist, the
 *  lines refine reads beside the stored quotes. */
export const EVIDENCE_USES = ["classify", "draft", "store", "leakAllow", "refine"] as const;
export type EvidenceUse = (typeof EVIDENCE_USES)[number];

/**
 * What a doubt's answer does to the issue it held once that issue is filed
 * (`meeting_ticket.answer_effect`): it stays as filed and loses its open
 * point (`confirm`), switches class (`reclass_ticket`, `reclass_epic`),
 * stays open as an idea epic (`reclass_idea`), is closed with a note
 * ("Nichts anlegen", `close`), takes in the second item of "Ein Thema"
 * (`fold`), or is that second item, closed with a link to the first
 * (`folded`).
 */
export const ANSWER_EFFECTS = [
  "confirm",
  "reclass_ticket",
  "reclass_epic",
  "reclass_idea",
  "close",
  "fold",
  "folded",
] as const;
export type AnswerEffect = (typeof ANSWER_EFFECTS)[number];

/** `meeting_ticket.answer_effect_state`: `applying` is the CAS claim,
 *  `applied` is final, `failed` may be tried again. */
export const ANSWER_EFFECT_STATES = ["pending", "applying", "applied", "failed"] as const;
export type AnswerEffectState = (typeof ANSWER_EFFECT_STATES)[number];

/** `meeting_ticket.answer_effect_failure`: why an effect did not reach the forge. */
export const ANSWER_EFFECT_FAILURES = [
  "no_token",
  "request_failed",
  "access_denied",
  "public_repo",
  "issue_not_found",
  "issue_moved",
] as const;
export type AnswerEffectFailure = (typeof ANSWER_EFFECT_FAILURES)[number];

/**
 * `meeting_ticket_link.kind`: `depends_on`, the from-row needs the to-row
 * done first (on GitLab the from-issue `is_blocked_by` the to-issue);
 * `relates_to`, the two are about the same work.
 */
export const TICKET_LINK_KINDS = ["depends_on", "relates_to"] as const;
export type TicketLinkKind = (typeof TICKET_LINK_KINDS)[number];

/**
 * `meeting_ticket_link.source`: what proposed the link. A grounded edge
 * (`edge`), the draft's `dependsOn` (`draft`), an open issue the inventory
 * named (`inventory`), the item's status report on an open issue
 * (`status`), a duplicate hint (`duplicate`), a row that is an issue the
 * meeting already filed (`same_meeting`), the two items of a
 * "one topic or two?" doubt (`pair`), "Ein Thema" (`merged`), or two of the
 * meeting's issues whose lines or texts overlap (`overlap`, 0044).
 */
export const TICKET_LINK_SOURCES = [
  "edge",
  "draft",
  "inventory",
  "status",
  "duplicate",
  "same_meeting",
  "pair",
  "merged",
  "overlap",
] as const;
export type TicketLinkSource = (typeof TICKET_LINK_SOURCES)[number];

/** `meeting_ticket_link.state`: `linking` is the CAS claim, `linked` and
 *  `skipped` are final, `failed` may be tried again. */
export const TICKET_LINK_STATES = ["pending", "linking", "linked", "failed", "skipped"] as const;
export type TicketLinkState = (typeof TICKET_LINK_STATES)[number];
